'use client';
import React, { useEffect } from 'react';
import Link from 'next/link';
import { ChevronDownIcon } from '@heroicons/react/24/outline'; // For dropdown icon
import { ALL_STATES } from '@/lib/location_states';
import { usePathname } from 'next/navigation';

const LocationNavigator: React.FC<{}> = () => {
  const pathname = usePathname();
  const [selectedState, setSelectedState] = React.useState<string>('/');

  const handleStateChange = (event: Event) => {
    const customEvent = event as CustomEvent;
    const newState = customEvent.detail;
    if (newState && Object.keys(ALL_STATES).includes(newState)) {
      // Perform any action based on the new state
      setSelectedState(newState);
    }
  };

  useEffect(() => {
    if (pathname.includes('state')) {
      setSelectedState(pathname.split('/')[2]); // Extract state from pathname
    } else if (pathname === '/') {
      setSelectedState('/');
    }
    window.addEventListener('selected_state', handleStateChange);

    return () => {
      window.removeEventListener('selected_state', handleStateChange);
    };
  }, [pathname]);

  useEffect(() => {
    if (selectedState !== '/') {
      let element = document.getElementById(`selected_state_${selectedState}`);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }
    }
  }, [selectedState]);

  return (
    <nav className='bg-[#F9F9F9] sticky top-(--header-height) h-(--state-nav-height) border-b border-gray-200 shadow-sm z-40'>
      {' '}
      {/* Adjust top based on header height */}
      <div className='flex h-full items-center overflow-x-auto whitespace-nowrap no-scrollbar px-4'>
        <Link href='/' key='home' prefetch={false}>
          <span
            className={`inline-flex px-4 border-b-2 py-2 text-sm font-medium cursor-pointer transition-colors duration-200 
            ${selectedState === '/' ? 'text-primary border-b-primary' : 'text-gray-700 hover:text-primary border-b-transparent hover:border-red-300'}`}
          >
            HOME
          </span>
        </Link>
        {Object.keys(ALL_STATES).map((state) => (
          <Link
            href={`/state/${state}`}
            key={state}
            id={`selected_state_${state}`}
            prefetch={false}
          >
            <span
              className={`inline-flex align-middle border-b-2 justify-center gap-x-2 px-4 py-2 text-sm font-medium cursor-pointer transition-colors duration-200 ${
                selectedState === state
                  ? 'text-primary border-b-2 border-primary'
                  : 'text-gray-700 hover:text-primary border-b-transparent hover:border-red-300'
              }`}
            >
              {ALL_STATES[state].label}{' '}
              {selectedState === state ? (
                <ChevronDownIcon className='text-primary h-4 text-sm font-medium ' />
              ) : null}
            </span>
          </Link>
        ))}
      </div>
    </nav>
  );
};

export default LocationNavigator;
